/**
 * CorrespondencePage - Sprint Y Block E + H (Language A Restyle)
 * 
 * Main page for correspondence management at /correspondence.
 * Features:
 * - Per-org list of all correspondence (org-scoped)
 * - FilterChips for status filtering (replaces Select dropdowns)
 * - Two-column split: list left, detail + timeline right
 * - Read-only HITL preview panel (no commit actions — Block G only)
 * 
 * Access: Org-scoped. Operators see ONLY their org's correspondence.
 * 
 * HITL: Visual distinction between PROPOSED and CONFIRMED links.
 * Block E is display-only. Edit/confirm is Block G (review queue).
 * 
 * FROZEN: ZERO write operations. All commits go through CorrespondenceReviewQueue.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { 
  Mail, Search, Clock, CheckCircle, AlertTriangle,
  RefreshCw, AlertCircle, Inbox, ExternalLink, Calendar,
  Shield, FileText, User, Plane, X
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { FilterChips } from "../components/shared/FilterChips";
import { CorrespondenceTimeline } from "../components/correspondence/CorrespondenceTimeline";
import { CorrespondenceCard } from "../components/correspondence/CorrespondenceCard";

export default function CorrespondencePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { translate: t, language } = useLanguage();
  
  // Data state
  const [correspondence, setCorrespondence] = useState([]);
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  
  // Detail panel state (read-only preview)
  const [selectedCorrespondence, setSelectedCorrespondence] = useState(null);
  
  // Fetch correspondence
  const fetchCorrespondence = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (statusFilter && statusFilter !== "all") {
        if (statusFilter === "needs_review") {
          params.append("needs_review", "true");
        } else {
          params.append("status", statusFilter);
        }
      }
      params.append("limit", "100");
      
      const response = await api.get(`/correspondence?${params.toString()}`);
      setCorrespondence(response.data);
    } catch (error) {
      console.error("Failed to fetch correspondence:", error);
      if (error.response?.status === 403) {
        toast.error(language === "cs" ? "Přístup odepřen" : "Access denied");
      } else {
        toast.error(t("messages.fetchError"));
      }
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, t, language]);
  
  // Fetch sources
  const fetchSources = useCallback(async () => {
    try {
      const response = await api.get("/correspondence/sources");
      setSources(response.data);
    } catch (error) {
      console.error("Failed to fetch sources:", error);
    }
  }, []);
  
  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get("/correspondence/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    fetchSources();
    fetchStats();
  }, [fetchSources, fetchStats]);
  
  // Fetch data when filters change
  useEffect(() => {
    fetchCorrespondence();
  }, [fetchCorrespondence]);
  
  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
    setSearchParams(params, { replace: true });
  }, [search, statusFilter, setSearchParams]);
  
  // Handle select item for detail panel
  const handleSelectItem = (item) => {
    setSelectedCorrespondence(item);
  };
  
  // Filter chips configuration
  const filterChipItems = useMemo(() => {
    const needsReviewCount = correspondence.filter(c => c.needs_review).length;
    const confirmedCount = correspondence.filter(c => c.confirmed_authorization_id || c.confirmed_mission_id || c.confirmed_pilot_id).length;
    const awaitingReplyCount = correspondence.filter(c => c.status === "pending_review" && c.direction === "outbound").length;
    
    return [
      { 
        id: "all", 
        label: t("correspondence.filterChips.all") || t("common.all") || "All", 
        count: correspondence.length, 
        variant: "neutral" 
      },
      { 
        id: "needs_review", 
        label: t("correspondence.filterChips.needsReview") || t("correspondence.needsReview") || "Needs Review", 
        count: needsReviewCount, 
        variant: "warning" 
      },
      { 
        id: "linked", 
        label: t("correspondence.filterChips.confirmed") || t("correspondence.confirmed") || "Confirmed", 
        count: confirmedCount, 
        variant: "neutral" 
      },
      { 
        id: "pending_review", 
        label: t("correspondence.filterChips.awaitingReply") || "Awaiting Reply", 
        count: awaitingReplyCount, 
        variant: "neutral" 
      }
    ];
  }, [t, correspondence]);
  
  // Filtered correspondence based on status filter
  const filteredCorrespondence = useMemo(() => {
    if (statusFilter === "all") return correspondence;
    if (statusFilter === "needs_review") return correspondence.filter(c => c.needs_review);
    if (statusFilter === "linked") return correspondence.filter(c => 
      c.confirmed_authorization_id || c.confirmed_mission_id || c.confirmed_pilot_id
    );
    return correspondence.filter(c => c.status === statusFilter);
  }, [correspondence, statusFilter]);
  
  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === "cs" ? "cs-CZ" : "en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };
  
  // Calculate days until deadline
  const getDaysUntilDeadline = (deadline) => {
    if (!deadline) return null;
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="space-y-6" data-testid="correspondence-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("correspondence.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("correspondence.subtitle")}</p>
        </div>
        
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => {
            fetchCorrespondence();
            fetchStats();
          }}
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          {t("actions.refresh")}
        </Button>
      </div>
      
      {/* Language A: 3 neutral StatTiles */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          {/* Awaiting Review (amber icon) */}
          <div className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <Clock className="w-5 h-5 text-amber-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-2xl font-semibold tabular-nums">{stats.pending_review || 0}</p>
              <p className="text-xs text-muted-foreground truncate">
                {t("correspondence.statTiles.awaitingReview") || t("correspondence.pendingReview")}
              </p>
            </div>
          </div>
          
          {/* Open Cases (blue icon) */}
          <div className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <Mail className="w-5 h-5 text-blue-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-2xl font-semibold tabular-nums">{stats.total || 0}</p>
              <p className="text-xs text-muted-foreground truncate">
                {t("correspondence.statTiles.openCases") || t("correspondence.total")}
              </p>
            </div>
          </div>
          
          {/* Deadline within 7 days (red icon) */}
          <div className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <AlertTriangle className="w-5 h-5 text-rose-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-2xl font-semibold tabular-nums">{stats.urgent_deadline || 0}</p>
              <p className="text-xs text-muted-foreground truncate">
                {t("correspondence.statTiles.deadlinesSoon") || "Deadline within 7 days"}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* FilterChips + Search */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <FilterChips
          items={filterChipItems}
          activeId={statusFilter}
          onSelect={setStatusFilter}
        />
        
        <div className="relative w-full sm:w-64 sm:ml-auto">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t("correspondence.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="search-input"
          />
          {search && (
            <button 
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
      
      {/* Two-column split: List left, Detail right */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: Correspondence List */}
        <div className="lg:col-span-2 space-y-2">
          <p className="text-sm text-muted-foreground mb-3">
            {filteredCorrespondence.length} {language === "cs" ? "položek" : "items"}
          </p>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredCorrespondence.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center border border-dashed border-slate-200 dark:border-slate-800 rounded-lg">
              <Mail className="w-10 h-10 text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">{t("correspondence.noCorrespondence")}</p>
            </div>
          ) : (
            <ScrollArea className="h-[calc(100vh-360px)]">
              <div className="space-y-2 pr-4">
                {filteredCorrespondence.map((item) => (
                  <CorrespondenceCard
                    key={item.id}
                    correspondence={item}
                    onViewDetails={handleSelectItem}
                    compact={true}
                    selected={selectedCorrespondence?.id === item.id}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
        
        {/* Right: Detail + HITL Preview + Timeline */}
        <div className="lg:col-span-3">
          {selectedCorrespondence ? (
            <Card className="border-slate-200 dark:border-slate-800">
              <CardContent className="p-5 space-y-5">
                {/* Header */}
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium truncate">{selectedCorrespondence.subject}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {selectedCorrespondence.sender_name || selectedCorrespondence.sender_email}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatDate(selectedCorrespondence.received_at)}
                    </p>
                  </div>
                  <button 
                    onClick={() => setSelectedCorrespondence(null)}
                    className="text-muted-foreground hover:text-foreground p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                
                <Separator />
                
                {/* HITL Preview Panel (READ-ONLY) */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Shield className="w-4 h-4 text-muted-foreground" />
                    {t("correspondence.extractedData")}
                  </h4>
                  
                  {/* Extracted fields */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {selectedCorrespondence.extracted?.reference_number && (
                      <div>
                        <p className="text-xs text-muted-foreground">{t("correspondence.referenceNumber")}</p>
                        <p className="font-mono text-sm">{selectedCorrespondence.extracted.reference_number}</p>
                      </div>
                    )}
                    {selectedCorrespondence.extracted?.deadline && (
                      <div>
                        <p className="text-xs text-muted-foreground">{t("correspondence.deadline")}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-sm">{formatDate(selectedCorrespondence.extracted.deadline)}</p>
                          {(() => {
                            const days = getDaysUntilDeadline(selectedCorrespondence.extracted.deadline);
                            if (days !== null && days <= 7) {
                              return (
                                <Badge variant="destructive" className="text-xs">
                                  {days <= 0 
                                    ? (t("correspondence.review?.overdue") || "Overdue")
                                    : `${days} ${t("correspondence.review?.daysLeft") || "days"}`
                                  }
                                </Badge>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Proposed Links - AMBER badge (HITL: not yet confirmed) */}
                  {(selectedCorrespondence.proposed_authorization_id || 
                    selectedCorrespondence.proposed_mission_id || 
                    selectedCorrespondence.proposed_pilot_id) && 
                   !selectedCorrespondence.confirmed_authorization_id &&
                   !selectedCorrespondence.confirmed_mission_id &&
                   !selectedCorrespondence.confirmed_pilot_id && (
                    <div className="p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                          {t("correspondence.proposedBadge")}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm">
                        {selectedCorrespondence.proposed_authorization_id && (
                          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                            <Shield className="w-3 h-3" />
                            <span>{t("correspondence.authorization")}</span>
                          </div>
                        )}
                        {selectedCorrespondence.proposed_mission_id && (
                          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                            <Plane className="w-3 h-3" />
                            <span>{t("correspondence.mission")}</span>
                          </div>
                        )}
                        {selectedCorrespondence.proposed_pilot_id && (
                          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                            <User className="w-3 h-3" />
                            <span>{t("correspondence.pilot")}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Confirmed Links - GREEN badge */}
                  {(selectedCorrespondence.confirmed_authorization_id || 
                    selectedCorrespondence.confirmed_mission_id || 
                    selectedCorrespondence.confirmed_pilot_id) && (
                    <div className="p-3 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                        <span className="text-sm font-medium text-green-700 dark:text-green-300">
                          {t("correspondence.confirmedBadge")}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm">
                        {selectedCorrespondence.confirmed_authorization_id && (
                          <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                            <Shield className="w-3 h-3" />
                            <span>{t("correspondence.authorization")}</span>
                          </div>
                        )}
                        {selectedCorrespondence.confirmed_mission_id && (
                          <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                            <Plane className="w-3 h-3" />
                            <span>{t("correspondence.mission")}</span>
                          </div>
                        )}
                        {selectedCorrespondence.confirmed_pilot_id && (
                          <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                            <User className="w-3 h-3" />
                            <span>{t("correspondence.pilot")}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Open in Review Queue button (NO commit action here) */}
                  {selectedCorrespondence.needs_review && (
                    <Button
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => navigate("/admin/correspondence-review")}
                      data-testid="open-review-queue-btn"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {t("correspondence.goToReviewQueue")}
                    </Button>
                  )}
                </div>
                
                <Separator />
                
                {/* Timeline section */}
                <div>
                  <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    {t("correspondence.timeline")}
                  </h4>
                  <CorrespondenceTimeline
                    correspondence={[selectedCorrespondence]}
                    onViewDetails={() => {}}
                    maxItems={1}
                    showLoadMore={false}
                  />
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 border border-dashed border-slate-200 dark:border-slate-800 rounded-lg">
              <Inbox className="w-10 h-10 text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">
                {t("correspondence.selectItem") || (language === "cs" ? "Vyberte položku" : "Select an item")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
